'use client';

import * as React from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Image from 'next/image';
import { X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { ContactForm } from '@/components/frontend/common/contact-form';
import { cn } from '@/lib/utils';

interface DemoItem {
    id: string;
    title: string;
    description: string;
    image?: string | null;
}

export function DemoItems({ initialItems }: { initialItems?: DemoItem[] } = {}) {
    const t = useTranslations('demoPage');
    const locale = useLocale();
    const [items, setItems] = React.useState<DemoItem[]>(() => initialItems ?? []);
    const [isLoading, setIsLoading] = React.useState(!initialItems?.length);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedDemo, setSelectedDemo] = React.useState<DemoItem | null>(null);

    React.useEffect(() => {
        if (initialItems?.length) {
            setItems(initialItems);
            setIsLoading(false);
            return;
        }
        const fetchDemoItems = async () => {
            try {
                setIsLoading(true);
                const res = await fetch(`/api/demo?locale=${encodeURIComponent(locale)}`, {
                    cache: 'no-store',
                });

                if (!res.ok) {
                    throw new Error('Failed to fetch demo items');
                }

                const data = await res.json();
                if (data?.items && Array.isArray(data.items) && data.items.length > 0) {
                    setItems(data.items);
                } else {
                    const staticPlans = t.raw('plans.items') as unknown;
                    const fallbackItems = Array.isArray(staticPlans)
                        ? (staticPlans as { title: string; description: string }[]).map((plan, index) => ({
                            id: `fallback-${index}`,
                            title: plan.title,
                            description: plan.description,
                            image: null,
                        }))
                        : [];
                    setItems(fallbackItems);
                }
            } catch (error) {
                try {
                    const staticPlans = t.raw('plans.items') as unknown;
                    const fallbackItems = Array.isArray(staticPlans)
                        ? (staticPlans as { title: string; description: string }[]).map((plan, index) => ({
                            id: `fallback-${index}`,
                            title: plan.title,
                            description: plan.description,
                            image: null,
                        }))
                        : [];
                    setItems(fallbackItems);
                } catch {
                    setItems([]);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchDemoItems();
    }, [locale, t, initialItems?.length]);

    return (
        <section className="border-b border-gray-200 dark:border-gray-900 bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-black dark:via-slate-950 dark:to-black">
            <div className="container mx-auto px-4 py-16 md:py-20">
                <div className="">
                    <div className="max-w-3xl mb-10 space-y-3">
                        <Typography variant="h2" className="!text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                            {t('plans.title')}
                        </Typography>
                        <Typography variant="p" className="text-gray-600 dark:text-gray-400">
                            {t('plans.subtitle')}
                        </Typography>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                        {isLoading ? (
                            <div className="text-center py-8 text-gray-600 dark:text-gray-400">Loading demo items...</div>
                        ) : items.length > 0 ? (
                            items.map((item) => (
                                <Card
                                    key={item.id}
                                    title={item.title}
                                    description={item.description}
                                    showFooterLink={false}
                                    className="bg-gradient-to-br from-white via-gray-50 to-white dark:from-slate-950 dark:via-slate-900 dark:to-black border border-gray-200 dark:border-slate-800"
                                    renderContent={
                                        <div className="flex flex-col md:flex-row md:items-center gap-6">
                                            {/* Left: Content */}
                                            <div className="flex-1 space-y-3">
                                                <Typography variant="h3" className="text-lg font-semibold text-gray-900 dark:text-white">{item.title}</Typography>
                                                <Typography variant="p" className="text-sm md:text-base leading-relaxed text-gray-600 dark:text-gray-300">
                                                    {item.description}
                                                </Typography>
                                                <div className="pt-4 inline-flex items-center gap-2 text-xs font-medium">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedDemo(item);
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="group/button relative inline-flex items-center gap-2 font-medium rounded-lg px-4 py-1.5 text-sm border-2 border-primary-500 dark:border-primary-400 bg-transparent text-primary-600 dark:text-primary-400 hover:bg-primary-700 dark:hover:bg-primary-700 hover:text-white dark:hover:text-white hover:border-primary-700 dark:hover:border-primary-700 hover:shadow-lg hover:shadow-primary-500/30 hover:-translate-y-0.5 transition-all duration-300"
                                                    >
                                                        <span>{t('plans.badgeAdminUser')}</span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Right: Image */}
                                            <div className="md:w-64 lg:w-80 xl:w-96 flex items-center justify-center">
                                                <div className="relative w-full aspect-video rounded-xl overflow-hidden">
                                                    {item.image ? (
                                                        <Image
                                                            src={item.image}
                                                            alt={item.title}
                                                            width={400}
                                                            height={225}
                                                            className="w-auto h-full object-cover"
                                                            loading="lazy"
                                                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
                                                        />
                                                    ) : (
                                                        <Image
                                                            src="/images/demo-placeholder.webp"
                                                            alt={item.title}
                                                            width={400}
                                                            height={225}
                                                            className="w-full h-full object-cover"
                                                            loading="lazy"
                                                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    }
                                />
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                                {t('plans.noItems', { default: 'No demo items available.' })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Demo Request Modal */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 z-[9999] bg-black/50 dark:bg-black/70 flex items-center justify-center p-4"
                    onClick={() => {
                        setIsModalOpen(false);
                        setSelectedDemo(null);
                    }}
                >
                    <div
                        className={cn(
                            'bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto',
                            'animate-in fade-in zoom-in-95 duration-200'
                        )}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {t('plans.modalTitle') || 'Request Demo'}
                                </h2>
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                    {t('plans.modalDescription') || 'We will contact you soon to schedule your demo.'}
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setSelectedDemo(null);
                                }}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                aria-label="Close modal"
                            >
                                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6">
                            {selectedDemo ? (
                                <ContactForm
                                    source="demo"
                                    demoName={selectedDemo.title}
                                    onClose={() => {
                                        setIsModalOpen(false);
                                        setSelectedDemo(null);
                                    }}
                                    className=""
                                />
                            ) : (
                                <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                                    Loading form...
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

